import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import { usePageLoader } from "../contexts/page_content_context";
import { useDependencyInjection } from "../../config/di/dependency_injection_context";
import { zodResolver } from "@hookform/resolvers/zod";
import { Autocomplete, Box, Divider, Drawer, Paper, TextField } from "@mui/material";
import { FuncionarioDropDown } from "../domain/models/funcionario_dropdown";
import { FuncionarioForm, FuncionarioSchema } from "../../features/rh/presentation/schemas/Funcionarios_cadastro/funcionario_schema";
import { FormDatePickerInput } from "./masked_inputs/react_forms/form_date_picker_input";
import { DropDownCargo } from "./dropdown_cargo";
import { DropDownCentroCusto } from "./dropdown_centro_custo";
import { FormSelectInput } from "./masked_inputs/react_forms/form_select_input";

import DynamicIcon from "./dynamic_icon";
import PrimaryButton from "./primary_button";
import DefaultFormInput from "./default_form_input";
import { processError } from "../utils/base_error";

type Props<F extends {}> = {
  name: keyof F;
  label?: string;
  newButtonLabel?: string;
  required?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  reactHookForm: UseFormReturn<F>;
};

export const DropDownFuncionario = <F extends {}>({
  name,
  label,
  newButtonLabel,
  required,
  readOnly,
  placeholder,
  reactHookForm,
}: Props<F>) => {
  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const { pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [conteudoDropDown, setConteudoDropDown] = useState<FuncionarioDropDown[]>([]);

  const {
    commons: { getFuncionariosDropDownUseCase },
    rh: { createFuncionarioUseCase },
  } = useDependencyInjection();

  const hookForm = useForm<FuncionarioForm>({
    reValidateMode: "onSubmit",
    mode: "onSubmit",
    disabled: saving,
    resolver: zodResolver(FuncionarioSchema),
  });

  //Load Centro de Custo DropDown

  const loadFuncionarioDropDown = useCallback(async () => {
    try {
      const funcionarios = await getFuncionariosDropDownUseCase
        .getFuncionariosDropDown(undefined, true, undefined, undefined);
      const selectedIdFuncionario = reactHookForm.getValues(name as any);
      if (selectedIdFuncionario && typeof selectedIdFuncionario === "number") {
        setConteudoDropDown(funcionarios.filter((x) => x.canBeSelected || x.idFuncionario === selectedIdFuncionario));
      } else {
        setConteudoDropDown(funcionarios.filter((x) => x.canBeSelected));
      }
    } catch (e: unknown) {
      console.error("Erro ao carregar funcionarios:", e);
    }
  }, [
    name,
    reactHookForm,
    getFuncionariosDropDownUseCase,
  ]);

  //Create Centro de Custo

  const hanldleSalvarCentroCusto = useCallback(
    async (form: FuncionarioForm) => {
      try {
        setSaving(true);

        const idCargo = await createFuncionarioUseCase.createFuncionario(
          form.idCargo,
          form.idCentroCusto,
          form.nome,
          form.sexo,
          form.dataAdmissao,
          form.regime,
          form.dataNascimento,
          form.demissaoIdDemissao,
          form.demissaoData,
          form.setor,
          form.avaliarHabilidadesEm,
          form.descontoSindical,
          form.filial,
          form.email1,
          form.email2,
          form.matricula,
          form.telefoneMovel,
          form.telefoneFixo,
          form.horarioTrabalho,
          form.horarioAlmoco,
          form.nacionalidade,
          form.naturalidade,
          form.estado,
          form.cpf,
          form.numeroPis,
          form.estadoCivil,
          form.salario,
          form.periodo,
          form.experiencia,
          form.prorrogarEm,
          form.aprovadoExperiencia,
          form.escolaridade,
          form.cargaHorarioMensal,
          form.valeTransporte,
          form.valorValeTransporte,
          form.ctps,
          form.ctpsSerie,
          form.ctpsEstado,
          form.ctpsDataEmissao,
          form.rg,
          form.rgDataExped,
          form.rgOrgaoExped,
          form.rgEstado,
          form.tituloEleitor,
          form.tituloZona,
          form.tituloSecao,
          form.reservista,
          form.nomeMae,
          form.nomePai,
          form.conjugeNome,
          form.conjugeRg,
          form.conjugeCpf,
          form.complementoCamisaInfo,
          form.complementoCalcaInfo,
          form.complementoCalcadoInfo,
          form.enderecoCep,
          form.enderecoCidade,
          form.enderecoEndereco,
          form.enderecoNumero,
          form.enderecoComplemento,
          form.enderecoBairro,
          form.enderecoEstado,
          form.enderecoPais,
          form.bancTipoInfo,
          form.bancBancoInfo,
          form.bancAgenciaInfo,
          form.bancContaInfo,
          form.bancContaDigitoInfo,
          form.bancOperacaoInfo,
          form.bancPixInfo,
          form.observacaoGeral,
          form.observacaoDocumento,
          form.demissaoData,
          form.fimAvisoPrevio,
          form.inicioAvisoPrevio,
          null,
          form.horarioTrabalhoSegQui,
          form.horarioTrabalhoSex,
        );

        reactHookForm.setValue(name as any, idCargo as any);
        pushInfoNotification("SUCESSO", "Centro custo criado com sucesso!!", { isAutoFinish: true });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao salvar funcionario"), { isAutoFinish: true });
      } finally {
        setSaving(false);
        loadFuncionarioDropDown();
        setOpenModal(false);
      }
    },
    [
      name,
      createFuncionarioUseCase,
      reactHookForm,
      loadFuncionarioDropDown,
      pushErrorNotification,
      pushInfoNotification,
    ],
  );

  useEffect(() => {
    requestAnimationFrame(() => loadFuncionarioDropDown());
  }, [loadFuncionarioDropDown]);

  // Add Centro Custo Button

  const CustomPaper = useMemo(
    () =>
      ({ children, ...props }: any) => (
        <Paper {...props}>
          <div>
            {children}
            <Divider />
            <div>
              <div
                className="py-2 px-4 cursor-pointer rounded-[4px] font-semibold flex items-center gap-2 text-sm text-primary-green hover:bg-hover-light-gray"
                onMouseDown={() => setOpenModal(true)}
                role="button"
                tabIndex={0}>
                <DynamicIcon nameIcon="Add" size={20} className="text-primary-green" />
                <span>{newButtonLabel ?? "Novo"}</span>
              </div>
            </div>
          </div>
        </Paper>
      ),
    [newButtonLabel],
  );

  return (
    <>
      {/* DropDown */}

      <Controller
        name={name as any}
        control={reactHookForm.control}
        render={({ field, formState }) => {
          let helperText: string | null = null;

          if (formState.errors[name as keyof typeof formState.errors]) {
            const fieldError = formState.errors[name] as any;
            if (fieldError?.message && typeof fieldError.message === "string") {
              helperText = fieldError.message;
            }
          }

          return (
            <Autocomplete
              size="small"
              getOptionDisabled={opt => !opt.canBeSelected}
              options={conteudoDropDown}
              getOptionKey={opt => opt.idFuncionario}
              getOptionLabel={opt => opt.nome ?? ""}
              isOptionEqualToValue={(opt, val) => opt.idFuncionario === val.idFuncionario}
              slots={{
                paper: CustomPaper,
              }}
              sx={{ minHeight: 45 }}
              readOnly={readOnly}
              disabled={formState.disabled}
              value={conteudoDropDown.find((x) => x.idFuncionario === field.value) ?? null}
              onChange={(_, newValue) => field.onChange(newValue ? newValue.idFuncionario : null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={label}
                  error={helperText !== null}
                  helperText={helperText}
                  placeholder={placeholder}
                  required={required}
                />
              )}
            />
          );
        }}
      />

      {/* Add Centro Custo Modal */}

      <Drawer
        anchor="right"
        open={openModal}
        onClose={() => setOpenModal(false)}
        style={{ zIndex: 1300 }}
        PaperProps={{ style: { width: 500, padding: 20, display: "flex", flexDirection: "column", height: "100%" } }}
      >
        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          <h2 className="text-lg font-semibold mb-4">Adicionar Funcionário</h2>

          <div className="flex flex-col items-start w-[25rem] gap-4">
            <DefaultFormInput name={"nome"} label={"Nome"} required={true} reactHookForm={hookForm} />

            <FormDatePickerInput name="dataAdmissao" label="Data da admissão" required={true} reactHookForm={hookForm} />
            <Box width={"100%"}>
              <DropDownCentroCusto name="idCentroCusto" required={true} readOnly={saving} reactHookForm={hookForm} />
            </Box>

            <FormSelectInput
              name="sexo"
              label="Sexo"
              required={true}
              options={[
                { id: "F", label: "Feminino" },
                { id: "M", label: "Masculino" },
              ]}
              reactHookForm={hookForm}
            />

            <DropDownCargo name="idCargo" required={true} reactHookForm={hookForm} />

            <FormDatePickerInput name="dataNascimento" label="Data de nascimento" reactHookForm={hookForm} />

            <FormSelectInput
              name="regime"
              label="Regime de Contratação"
              options={[
                { id: "C", label: "CLT" },
                { id: "E", label: "Estagiário" },
                { id: "P", label: "PJ" },
                { id: "D", label: "Diarista" },
                { id: "S", label: "Sócio" },
                { id: "J", label: "Jovem" },
              ]}
              reactHookForm={hookForm}
            />

            <DefaultFormInput name={"setor"} label={"Setor"} reactHookForm={hookForm} />

            <DefaultFormInput name={"filial"} label={"Filial"} reactHookForm={hookForm} />
          </div>
        </Box>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            background: "white",
            padding: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold"
            onClick={() => setOpenModal(false)}
          >
            <h1 className="text-sm text-black dark:text-white">Cancelar</h1>
          </Box>

          <Box width="120px">
            {saving ? (
              <PrimaryButton buttonColor="primary-green">Salvando...</PrimaryButton>
            ) : (
              <PrimaryButton buttonColor="primary-green" onClick={hookForm.handleSubmit(hanldleSalvarCentroCusto)}>
                Salvar
              </PrimaryButton>
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
